import { PropsWithChildren } from 'react';
import styles from './styles.module.scss';
import { Portal } from '@starsoft/common/components/Portal';
import { TransactionsCommonMappingModalLayoutProps } from './props';
import { motion } from 'framer-motion';
import { transactionMappingVariants } from './variants';

/**
 * TransactionsCommonMappingModalLayout Component
 * This component is responsible for rendering the layout of the transaction mapping modal.
 * It uses the Portal component to render the modal in a different part of the DOM tree,
 * and the motion.div component from framer-motion to handle animations.
 *
 * @param {PropsWithChildren<TransactionsCommonMappingModalLayoutProps>} props - The properties for the TransactionsCommonMappingModalLayout component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal layout.
 * @param {DOMRect | undefined} props.boundings - The bounding rectangle of the button for modal positioning.
 *
 * @returns {JSX.Element} The rendered component displaying the modal layout with transaction mapping content.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModalLayout boundings={buttonRef.current?.getBoundingClientRect()}>
 *   <div>Modal Content</div>
 * </TransactionsCommonMappingModalLayout>
 */
export function TransactionsCommonMappingModalLayout({
  children,
  boundings,
}: PropsWithChildren<TransactionsCommonMappingModalLayoutProps>) {
  return (
    <Portal>
      <motion.div
        variants={transactionMappingVariants(boundings)}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={styles.container}
      >
        {children}
      </motion.div>
    </Portal>
  );
}
